import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cn } from '../lib/cn';

export type ButtonVariant =
  'secondary' | 'primary' | 'dark' | 'ghost' | 'danger' | 'danger-outline' | 'link' | 'dashed';
export type ButtonSize = 'sm' | 'base' | 'lg';

export interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  block?: boolean;
  asChild?: boolean;
  type?: 'button' | 'submit' | 'reset';
}

const variantClass = (v: ButtonVariant = 'secondary') => (v === 'secondary' ? '' : `btn-${v}`);
const sizeClass = (s: ButtonSize = 'base') => (s === 'base' ? '' : `btn-${s}`);

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'secondary',
      size = 'base',
      isLoading = false,
      block = false,
      asChild = false,
      type = 'button',
      disabled,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp: React.ElementType = asChild ? Slot : 'button';
    return (
      <Comp
        ref={ref}
        type={asChild ? undefined : type}
        className={cn(
          'btn',
          variantClass(variant),
          sizeClass(size),
          block && 'btn-block',
          isLoading && 'is-loading',
          className,
        )}
        aria-busy={isLoading || undefined}
        disabled={isLoading || disabled}
        {...props}
      >
        {children}
      </Comp>
    );
  },
);
Button.displayName = 'Button';
